import React from 'react';

interface ReviewStepProps {
    data: {
        name: string;
        description: string;
        primaryUseCase: string;
        personality: {
            tone: string;
            formality: string;
            responseLength: string;
        };
        knowledge: string[];
        uploadedFiles: File[];
        appearance: {
            theme: string;
            primaryColor: string;
            position: string;
        };
    };
}

const ReviewStep: React.FC<ReviewStepProps> = ({ data }) => {
    // Helfer-Funktion zur Umwandlung von IDs in lesbare Namen
    const getPrimaryUseCaseName = (id: string): string => {
        const cases: Record<string, string> = {
            'customer-service': 'Kundenservice',
            'sales': 'Vertrieb',
            'internal': 'Interne Nutzung',
            'education': 'Bildung & Training',
            'entertainment': 'Unterhaltung',
            'other': 'Sonstiges'
        };
        return cases[id] || id;
    };

    const getToneName = (id: string): string => {
        const tones: Record<string, string> = {
            'friendly': 'Freundlich',
            'professional': 'Professionell',
            'casual': 'Locker',
            'enthusiastic': 'Enthusiastisch'
        };
        return tones[id] || id;
    };

    const getThemeName = (id: string): string => {
        const themes: Record<string, string> = {
            'light': 'Hell',
            'dark': 'Dunkel',
            'system': 'System'
        };
        return themes[id] || id;
    };

    const getResponseLengthName = (id: string): string => {
        const lengths: Record<string, string> = {
            'concise': 'Kurz und knapp',
            'moderate': 'Mittellang',
            'detailed': 'Ausführlich'
        };
        return lengths[id] || id;
    };

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-medium text-gray-900">Überprüfe deinen Chatbot</h2>
            <p className="text-gray-500">
                Schaue dir die Einstellungen deines Chatbots noch einmal an, bevor du ihn erstellst. Du kannst später jederzeit Änderungen vornehmen.
            </p>

            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Grundinformationen</h3>
                    <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Name</dt>
                            <dd className="mt-1 text-sm text-gray-900">{data.name || 'Nicht angegeben'}</dd>
                        </div>
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Haupteinsatzgebiet</dt>
                            <dd className="mt-1 text-sm text-gray-900">{getPrimaryUseCaseName(data.primaryUseCase)}</dd>
                        </div>
                        <div className="sm:col-span-2">
                            <dt className="text-sm font-medium text-gray-500">Beschreibung</dt>
                            <dd className="mt-1 text-sm text-gray-900">{data.description || 'Keine Beschreibung angegeben'}</dd>
                        </div>
                    </dl>
                </div>

                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Persönlichkeit</h3>
                    <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Tonfall</dt>
                            <dd className="mt-1 text-sm text-gray-900">{getToneName(data.personality.tone)}</dd>
                        </div>
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Formalität</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                                {data.personality.formality === 'formal' ? 'Formell (Sie-Form)' : 'Informell (Du-Form)'}
                            </dd>
                        </div>
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Antwortlänge</dt>
                            <dd className="mt-1 text-sm text-gray-900">{getResponseLengthName(data.personality.responseLength)}</dd>
                        </div>
                    </dl>
                </div>

                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Wissensquellen</h3>
                    <dl className="mt-4 grid grid-cols-1 gap-y-4">
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Webseiten</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                                {data.knowledge.length > 0 ? (
                                    <ul className="list-disc pl-5 space-y-1">
                                        {data.knowledge.map((source, index) => (
                                            <li key={index}>{source}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <span className="text-gray-500 italic">Keine Webseiten angegeben</span>
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-sm font-medium text-gray-500">Dokumente</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                                {data.uploadedFiles.length > 0 ? (
                                    <ul className="list-disc pl-5 space-y-1">
                                        {data.uploadedFiles.map((file, index) => (
                                            <li key={index}>{file.name}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <span className="text-gray-500 italic">Keine Dokumente hochgeladen</span>
                                )}
                            </dd>
                        </div>
                    </dl>
                </div>

                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Erscheinungsbild</h3>
                    <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Theme</dt>
                            <dd className="mt-1 text-sm text-gray-900">{getThemeName(data.appearance.theme)}</dd>
                        </div>
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Primärfarbe</dt>
                            <dd className="mt-1 flex items-center">
                                <div
                                    className="h-5 w-5 rounded-full border border-gray-300 mr-2"
                                    style={{ backgroundColor: data.appearance.primaryColor }}
                                ></div>
                                <span className="text-sm text-gray-900">{data.appearance.primaryColor}</span>
                            </dd>
                        </div>
                        <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Position</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                                {data.appearance.position === 'right' ? 'Rechts unten' : 'Links unten'}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-400 p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-green-700">
                            Dein Chatbot ist bereit zur Erstellung! Klicke auf "Chatbot erstellen", um fortzufahren.
                            Du kannst alle Einstellungen später in der Chatbot-Administration anpassen.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewStep;